import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import {
  clearSelectedAvailability,
  clearSelectedTime,
  createAvailabilityReducer,
  initialAvailabilityState,
  setAvailabilities,
  setFloorPlans,
  setSelectedAvailability,
  setSelectedDate,
  setSelectedFloorPlanId,
  setSelectedFloorPlanListingIds,
  setSelectedGuestCount,
  setSelectedTime,
} from '@shared/availability/availabilityReducer';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { reportAppError } from '@shared/reportAppError';
import { type FloorPlanData } from '@shared/types/floorPlans';
import { todayInTimezone, toISODateFormat } from '@shared/utils/dateFormatters';
import { zonedDateTime } from '@shared/utils/dateParsers';
import {
  getFloorPlanByNameSlug,
  getFloorPlansForRestaurantId,
} from '../floorPlans/apiHelpers';
import { type Availability, getAvailabilities } from '../restaurant/apiHelpers';
import {
  calculateFilteredData,
  findBestAvailableForTime,
  getAvailabilitiesGroupedByTime,
  getAvailabilitiesGroupedByTimeForTimePicker,
  getSelectedFloorPlan,
  toSelectedAvailability,
} from './availabilityDerivedState';
import { useRestaurantContext } from './RestaurantContext';

export interface TSelectedAvailability {
  isOffer: boolean;
  isResale?: boolean;
  isBestAvailable: boolean;
  listingId: string;
  purchasePrice: number | null;
  standardPrice?: number | null;
  publicName: string;
  time: string;
  date: string;
}

/**
 * Whether the selected table can still be booked at the price the guest saw.
 * `changed` and `unavailable` block booking until the guest acts on them.
 */
export type SelectedPriceStatus =
  | { kind: 'current' }
  | { kind: 'changed'; previousPrice: number; newPrice: number }
  | { kind: 'unavailable' };

const PRICE_STATUS_CURRENT: SelectedPriceStatus = { kind: 'current' };

export interface AvailabilityContextState {
  availabilities: Availability[];
  availableTimes: string[];
  hasAvailableTimesForGuestCount: boolean;
  hasNoAvailableTimesForSelectedGuestCount: boolean;
  clearSelectedAvailability: () => void;
  clearSelectedTime: () => void;
  expandedAvailabilityListingIds: string[];
  expandedAvailabilities: Availability[];
  filteredAvailabilities: Availability[];
  filteredAvailabilitiesGroupedByTime: Record<string, Availability[]>;
  timePickerAvailabilitiesGroupedByTime: Record<string, Availability[]>;
  hasAvailabilitiesOnGivenDay: boolean;
  hasUnsupportedGuestCount: boolean;
  isAvailabilitiesFetchLoading: boolean;
  isFloorPlansFetchLoading: boolean;
  isSelectedDateToday: boolean;
  selectedAvailability: TSelectedAvailability | null;
  floorPlans: FloorPlanData[];
  selectedFloorPlan: FloorPlanData;
  setSelectedFloorPlanId: (floorPlanId: string) => void;
  selectedDate: Date;
  selectedFloorPlanListingIds: string[];
  selectedGuestCount: number;
  selectedTime: string | null;
  preferredTime: string | null;
  setSelectedAvailability: (availability: TSelectedAvailability) => void;
  setSelectedDate: (date: Date) => void;
  setSelectedFloorPlanListingIds: (ids: string[]) => void;
  setSelectedGuestCount: (guestCount: number) => void;
  setSelectedTime: (time: string) => void;
  setPreferredTime: (time: string | null) => void;
  reselectBestAvailableForSelectedTime: () => void;
  selectedPriceStatus: SelectedPriceStatus;
  /** The guest accepted the new price shown for the selected table. */
  confirmPriceChange: () => void;
  /**
   * Silently refetches the selected date's availabilities and re-checks the
   * selected table against them. Resolves to the resulting price status.
   */
  refreshAvailabilities: () => Promise<SelectedPriceStatus>;
}

export const AvailabilityContext =
  createContext<AvailabilityContextState | null>(null);
AvailabilityContext.displayName = 'AvailabilityContext';

export const useAvailabilityContext = () =>
  useDefinedContext(AvailabilityContext);

interface AvailabilityContextProviderProps {
  children: ReactNode;
}

export const AvailabilityContextProvider = ({
  children,
}: AvailabilityContextProviderProps) => {
  const { restaurantDetails } = useRestaurantContext();

  const guestAvailabilityReducer = createAvailabilityReducer<
    Availability,
    TSelectedAvailability
  >();

  const { floorPlanNameSlug, reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const dateSearchParam = searchParams.get('date');
  const guestCountSearchParam = searchParams.get('guestCount');
  const timeSearchParam = searchParams.get('time');
  const hasAppliedTimeSearchParamRef = useRef(false);
  const [preferredTime, setPreferredTime] = useState<string | null>(() =>
    searchParams.get('preferredTime'),
  );

  const selectedDateFromSearchParams = dateSearchParam
    ? zonedDateTime(dateSearchParam, '00:00:00', restaurantDetails.timezone)
    : undefined;

  const [state, dispatch] = useReducer(
    guestAvailabilityReducer,
    initialAvailabilityState<Availability, TSelectedAvailability>(
      restaurantDetails.timezone,
      selectedDateFromSearchParams,
      Number(guestCountSearchParam),
    ),
  );
  const {
    availabilities,
    selectedFloorPlanId,
    selectedGuestCount,
    selectedTime,
    selectedDate,
    selectedFloorPlanListingIds,
    selectedAvailability,
    floorPlans,
  } = state;

  const { isPending: isFloorPlansFetchLoading } = useAbortEffect(
    {
      effect: async (signal) => {
        try {
          const allFloorPlans = floorPlanNameSlug
            ? [
                await getFloorPlanByNameSlug(
                  restaurantDetails.id,
                  floorPlanNameSlug,
                  signal,
                ),
              ]
            : await getFloorPlansForRestaurantId(restaurantDetails.id, signal);
          dispatch(setFloorPlans(allFloorPlans));
          dispatch(setSelectedFloorPlanId(allFloorPlans[0].id));

          return allFloorPlans;
        } catch {
          dispatch(
            setFloorPlans([
              {
                id: 'not-loaded',
                backgroundSrc: undefined,
                floorPlanTables: [],
                name: 'not loaded',
              },
            ]),
          );
          dispatch(setSelectedFloorPlanId('not-loaded'));

          return [
            {
              id: 'not-loaded',
              backgroundSrc: undefined,
              floorPlanTables: [],
              name: 'not loaded',
            },
          ];
        }
      },
      onError: (e) => {
        if (e instanceof ApiError) {
          errorToast({
            message: e.message,
          });
        } else {
          errorToast({
            message: 'Failed to fetch floors plans. Please try again.',
          });
        }
        reportAppError(e);
      },
      throwOnError: false,
    },
    [],
  );

  const { isPending: isAvailabilitiesFetchLoading } =
    useAbortEffect(async () => {
      const allAvailabilities = await getAvailabilities({
        restaurantId: restaurantDetails.id,
        date: toISODateFormat(selectedDate),
        guestCount: selectedGuestCount,
        changingReservationId: reservationId,
      });
      dispatch(setAvailabilities(allAvailabilities));
    }, [selectedDate, selectedGuestCount, restaurantDetails.id, reservationId]);

  useEffect(() => {
    if (isFloorPlansFetchLoading || isAvailabilitiesFetchLoading) return;

    const firstFloorPlanWithAvailabilities = floorPlans.find((floorPlan) =>
      availabilities.some(
        (availability) => availability.listing.floorPlanId === floorPlan.id,
      ),
    );
    if (
      !firstFloorPlanWithAvailabilities ||
      firstFloorPlanWithAvailabilities.id === selectedFloorPlanId
    ) {
      return;
    }

    dispatch(setSelectedFloorPlanId(firstFloorPlanWithAvailabilities.id));
  }, [isFloorPlansFetchLoading, isAvailabilitiesFetchLoading]);

  const [selectedPriceStatus, setSelectedPriceStatus] =
    useState<SelectedPriceStatus>(PRICE_STATUS_CURRENT);
  // Read by refreshAvailabilities, which outlives the render that created it.
  const latestRef = useRef({ state, selectedPriceStatus });
  latestRef.current = { state, selectedPriceStatus };

  // A different table starts with a clean slate; price updates to the same
  // table keep their status.
  const selectedTableKey = selectedAvailability
    ? `${selectedAvailability.listingId}|${selectedAvailability.date}|${selectedAvailability.time}`
    : null;
  // The price the guest last saw and accepted for the selected table. Price
  // updates rewrite the selection, so it is tracked separately: a table that
  // vanishes and returns at a new price must still be confirmed.
  const acceptedPriceRef = useRef<number | null>(null);
  useEffect(() => {
    setSelectedPriceStatus(PRICE_STATUS_CURRENT);
    acceptedPriceRef.current =
      latestRef.current.state.selectedAvailability?.purchasePrice ?? null;
  }, [selectedTableKey]);

  const refreshAvailabilities =
    useCallback(async (): Promise<SelectedPriceStatus> => {
      const requested = latestRef.current.state;
      const date = toISODateFormat(requested.selectedDate);
      const guestCount = requested.selectedGuestCount;

      try {
        const fresh = await getAvailabilities({
          restaurantId: restaurantDetails.id,
          date,
          guestCount,
          changingReservationId: reservationId,
        });

        const { state: latest, selectedPriceStatus: currentStatus } =
          latestRef.current;
        // The guest moved to another date or party size while this was in flight.
        if (
          toISODateFormat(latest.selectedDate) !== date ||
          latest.selectedGuestCount !== guestCount
        ) {
          return currentStatus;
        }

        dispatch(setAvailabilities(fresh));

        const selected = latest.selectedAvailability;
        if (!selected || selected.purchasePrice === null) {
          return PRICE_STATUS_CURRENT;
        }

        const match = fresh.find(
          (availability) =>
            availability.listing.id === selected.listingId &&
            availability.time === selected.time &&
            availability.date === selected.date,
        );
        let status: SelectedPriceStatus;
        if (!match || match.purchasePrice === null) {
          status = { kind: 'unavailable' };
        } else {
          const acceptedPrice =
            acceptedPriceRef.current ?? selected.purchasePrice;
          status =
            match.purchasePrice === acceptedPrice
              ? PRICE_STATUS_CURRENT
              : {
                  kind: 'changed',
                  previousPrice: acceptedPrice,
                  newPrice: match.purchasePrice,
                };
          if (match.purchasePrice !== selected.purchasePrice) {
            dispatch(
              setSelectedAvailability({
                ...selected,
                purchasePrice: match.purchasePrice,
                standardPrice: match.standardPrice ?? null,
              }),
            );
          }
        }
        setSelectedPriceStatus(status);
        return status;
      } catch (error) {
        reportAppError(error);
        return latestRef.current.selectedPriceStatus;
      }
    }, [restaurantDetails.id, reservationId]);

  const confirmPriceChange = useCallback(() => {
    const { selectedPriceStatus: status } = latestRef.current;
    if (status.kind !== 'changed') return;
    acceptedPriceRef.current = status.newPrice;
    setSelectedPriceStatus(PRICE_STATUS_CURRENT);
  }, []);

  const {
    expandedAvailabilities,
    filteredAvailabilities,
    // TODO: What even is this?
    expandedAvailabilityListingIds,
    hasUnsupportedGuestCount,
  } = useMemo(
    () => calculateFilteredData(state, restaurantDetails.timezone),
    [
      // Only re-calculate if these values change that are the values that affect the filtered data inside calculateFilteredData
      availabilities,
      selectedDate,
      selectedFloorPlanId,
      selectedFloorPlanListingIds,
      selectedGuestCount,
      selectedTime,
      restaurantDetails.timezone,
    ],
  );

  useEffect(() => {
    if (
      selectedFloorPlanListingIds.length === 0 ||
      filteredAvailabilities.length !== 1
    ) {
      return;
    }

    const availability = filteredAvailabilities[0];
    dispatch(setSelectedAvailability(toSelectedAvailability(availability)));
  }, [selectedFloorPlanListingIds, filteredAvailabilities]);

  const filteredAvailabilitiesGroupedByTime = getAvailabilitiesGroupedByTime(
    state,
    restaurantDetails.timezone,
  );
  const timePickerAvailabilitiesGroupedByTime =
    getAvailabilitiesGroupedByTimeForTimePicker(
      state,
      restaurantDetails.timezone,
    );
  const availableTimes = Object.keys(filteredAvailabilitiesGroupedByTime);
  const hasAvailableTimesForGuestCount = Boolean(availableTimes.length);
  const hasNoAvailableTimesForSelectedGuestCount = !availableTimes.length;

  const isSelectedDateToday =
    todayInTimezone(restaurantDetails.timezone) ===
    toISODateFormat(selectedDate);

  const selectBestAvailableForTime = (time: string) => {
    const bestAvailable = findBestAvailableForTime(availabilities, time);

    if (!bestAvailable) {
      return;
    }

    dispatch(setSelectedAvailability(toSelectedAvailability(bestAvailable)));
  };

  const handleTimeSelection = (time: string) => {
    dispatch(setSelectedTime(time));
    selectBestAvailableForTime(time);
  };

  const reselectBestAvailableForSelectedTime = () => {
    if (!selectedTime) {
      return;
    }

    selectBestAvailableForTime(selectedTime);
  };

  useEffect(() => {
    if (
      hasAppliedTimeSearchParamRef.current ||
      isAvailabilitiesFetchLoading ||
      !timeSearchParam ||
      selectedTime
    ) {
      return;
    }

    const pickerTimes = Object.keys(timePickerAvailabilitiesGroupedByTime);

    if (!pickerTimes.includes(timeSearchParam)) {
      return;
    }

    hasAppliedTimeSearchParamRef.current = true;
    dispatch(setSelectedTime(timeSearchParam));
    selectBestAvailableForTime(timeSearchParam);
  }, [
    availabilities,
    isAvailabilitiesFetchLoading,
    timeSearchParam,
    selectedTime,
    timePickerAvailabilitiesGroupedByTime,
  ]);

  const value = useMemo<AvailabilityContextState>(
    () => ({
      availabilities,
      availableTimes,
      hasAvailableTimesForGuestCount,
      hasNoAvailableTimesForSelectedGuestCount,
      clearSelectedAvailability: () => dispatch(clearSelectedAvailability()),
      clearSelectedTime: () => dispatch(clearSelectedTime()),
      filteredAvailabilities,
      filteredAvailabilitiesGroupedByTime,
      timePickerAvailabilitiesGroupedByTime,
      hasAvailabilitiesOnGivenDay: !!availabilities.length,
      hasUnsupportedGuestCount,
      isAvailabilitiesFetchLoading,
      isFloorPlansFetchLoading,
      selectedAvailability,
      selectedDate,
      selectedGuestCount,
      selectedFloorPlanListingIds,
      selectedTime,
      preferredTime,
      setSelectedAvailability: (availability: TSelectedAvailability) =>
        dispatch(setSelectedAvailability(availability)),
      // A time selected on one date has no bearing on another — clearing it
      // (rather than just `selectedAvailability`) lets the auto-select effect
      // on the Book screen re-anchor on the new date's own availabilities.
      // Leaving the old time in place made that effect skip re-selecting
      // (it only acts when `selectedTime` is empty), stranding
      // `selectedAvailability` at null until the guest reselected a time by
      // hand.
      setSelectedDate: (date: Date) => {
        dispatch(setSelectedDate(date));
        dispatch(clearSelectedTime());
      },
      setSelectedFloorPlanListingIds: (floorPlanListingIds: string[]) =>
        dispatch(setSelectedFloorPlanListingIds(floorPlanListingIds)),
      setSelectedGuestCount: (guestCount: number) =>
        dispatch(setSelectedGuestCount(guestCount)),
      setSelectedTime: handleTimeSelection,
      setPreferredTime,
      reselectBestAvailableForSelectedTime,
      selectedPriceStatus,
      confirmPriceChange,
      refreshAvailabilities,
      expandedAvailabilityListingIds,
      expandedAvailabilities,
      isSelectedDateToday,
      floorPlans,
      selectedFloorPlan: getSelectedFloorPlan(state),
      setSelectedFloorPlanId: (floorPlanId: string) =>
        dispatch(setSelectedFloorPlanId(floorPlanId)),
    }),
    [
      state,
      availabilities,
      floorPlans,
      isAvailabilitiesFetchLoading,
      isFloorPlansFetchLoading,
      preferredTime,
      searchParams,
      selectedPriceStatus,
      refreshAvailabilities,
      confirmPriceChange,
    ],
  );

  return (
    <AvailabilityContext.Provider value={value}>
      {children}
    </AvailabilityContext.Provider>
  );
};
